import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Postdata } from "../../helpers/api.service";
import HttpService from "../../helpers/httpServices";
import { isPermission, isRole, isRoleType } from "../../redux/selectors/authSelector";
import Error403Page from "../ErrorPage";
import Board_List from "../Board-Management/Board_List";
import SchoolManagement from "../School-Management/School_List";
import TeacherManagement from "../teacher-Management/Teacher_List";
import Admissions from "../Admission/Admissions_main";
import NewsManagement from "../News-Management/NewsManagement_main";
import Education from "../Education/EducationMain";
import Department from "../Department/Department_main";
import Extracurricular from "../Extracurricular/Extracurricular_main"
import ParentList from "../Parent-Management/ParentList";
import CommunicationList from "../Communication/CommunicationList";
import { FiMoreHorizontal } from "react-icons/fi";

const ViewSchool = () => {
  const { id: schoolId } = useParams();
  const permissions = useSelector(isPermission);
  const role = useSelector(isRole);
  const role_type = useSelector(isRoleType);

  const location = useLocation();
  const mainTabFromState = location.state?.mainTab;
  const navigate = useNavigate();
  // Add these lines - fetch country code from school data or localStorage
  const [countryCode, setCountryCode] = useState(
    location.state?.countryCode || localStorage.getItem("activeCountryCode") || "KE"
  );

  // Determine board management label based on country
  const [boardManagementLabel, setBoardManagementLabel] = useState(() =>
    countryCode?.toUpperCase() === "UG"
      ? "School Management Committee"
      : "Board Management",
  );

  useEffect(() => {
    setBoardManagementLabel(countryCode?.toUpperCase() === "UG"
      ? "School Management Committee"
      : "Board Management",)
  }, [countryCode])

  // State management
  const [activeTab, setActiveTab] = useState(mainTabFromState || "School Details");
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visibleTabs, setVisibleTabs] = useState([]);
  const [hiddenTabs, setHiddenTabs] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Refs
  const tabContainerRef = useRef(null);
  const dropdownRef = useRef(null);
  const tabRefs = useRef([]);

  useEffect(() => {

    if (schoolId) {
      localStorage.setItem("activeSchoolId", schoolId);
    }
  }, [schoolId]);

  // Check access permission
  const isAccessible = useMemo(
    () => role === "admin" || role === "subadmin" || role == "schooladmin" || permissions?.includes("view_School"),
    [role, permissions]
  );

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    setIsDropdownOpen(false);

    // Tabs that should NOT persist navigation state
    const noStateTabs = ["Education", "Extracurricular"];

    if (noStateTabs.includes(tabName)) {
      navigate(location.pathname, { replace: true });
      return;
    }

    navigate(location.pathname, {
      replace: true,
      state: {
        ...location.state,
        mainTab: tabName,
        countryCode: countryCode,  // Preserve country code
      },
    });
  };

  // Navigation items configuration - WRAPPED IN useMemo TO FIX INFINITE LOOP
  const navigationItems = useMemo(() => [
    {
      name: "School Details",
      component: null,
    },
    {
      name: boardManagementLabel,
      component: <Board_List schoolId={schoolId} />,
    },
    {
      name: "Management",
      component: <SchoolManagement schoolId={schoolId} />,
    },
    {
      name: "Teacher Management",
      component: <TeacherManagement schoolId={schoolId} />,
    },
    {
      name: "Admissions",
      component: <Admissions schoolId={schoolId} />,
    },
    {
      name: "News Management",
      component: <NewsManagement schoolId={schoolId} />,
    },
    {
      name: "Education",
      component: <Education schoolId={schoolId} />,
    },
    {
      name: "Department",
      component: <Department schoolId={schoolId} />,
    },
    {
      name: "Extracurricular",
      component: <Extracurricular schoolId={schoolId} />,
    },
    {
      name: "Parents Management",
      component: <ParentList schoolId={schoolId} />,
    },
    {
      name: "Communication",
      component: <CommunicationList schoolId={schoolId} />,
    },
  ], [schoolId, boardManagementLabel]);

  useEffect(() => {
    console.log("boardManagementLabel:-", boardManagementLabel, schoolId)
  }, [boardManagementLabel])

  useLayoutEffect(() => {
    console.log('navigationItems:-', navigationItems.length, tabContainerRef?.current, tabContainerRef?.current?.offsetWidth)
    if (!tabContainerRef.current) return;
    if (!navigationItems.length) return;

    const calculateVisibleTabs = () => {
      const containerWidth = tabContainerRef.current.offsetWidth;
      if (!containerWidth) return;

      const gap = 8;
      const moreButtonWidth = 48;

      let usedWidth = 0;
      const tempVisible = [];
      const hidden = [];

      navigationItems.forEach((item) => {
        const tabWidth = item.name.length * 8 + 24;

        if (usedWidth + tabWidth + gap <= containerWidth) {
          tempVisible.push(item);
          usedWidth += tabWidth + gap;
        } else {
          hidden.push(item);
        }
      });

      if (hidden.length > 0) {
        while (
          tempVisible.length > 0 &&
          usedWidth + moreButtonWidth > containerWidth
        ) {
          const removed = tempVisible.pop();
          hidden.unshift(removed);
          usedWidth -= removed.name.length * 8 + 32 + gap;
        }
      }

      setVisibleTabs(tempVisible);
      setHiddenTabs(hidden);
    };

    requestAnimationFrame(calculateVisibleTabs);

    const resizeObserver = new ResizeObserver(calculateVisibleTabs);
    resizeObserver.observe(tabContainerRef.current);

    return () => resizeObserver.disconnect();
  }, [navigationItems, schoolData]);



  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  // Fetch school details
  const fetchSchoolData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Postdata(HttpService.admin_school_show, { id: schoolId });
      if (res.status === 200) {
        setSchoolData(res.data);

        // Set country code from school data
        if (res.data.country_code) {
          setCountryCode(res.data.country_code);
          localStorage.setItem("activeCountryCode", res.data.country_code);
        }
      }
    } catch (error) {
      console.error("Error fetching school data:", error);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    fetchSchoolData();
  }, [fetchSchoolData]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [activeTab]);

  // Guards
  if (!isAccessible) return <Error403Page />;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-600">
        Loading details...
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="flex items-center justify-center h-screen text-red-500">
        No data found.
      </div>
    );
  }

  // Render functions
  const renderField = (label, value, className = "") => (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-500 mb-1">{label}</label>
      <div className="p-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-800 font-medium">
        {value}
      </div>
    </div>
  );

  const renderSchoolDetails = () => (
    <div className="bg-white shadow-lg rounded-xl p-8">
      <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-4">
        School Information
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderField("First Name", schoolData.first_name)}
        {renderField("Last Name", schoolData.last_name)}
        {renderField("Username", schoolData.username)}
        {renderField("School Name", schoolData.name)}
        {renderField("Email", schoolData.email)}
        {renderField("Mobile Number", schoolData.mobile_number)}
        {renderField("Sub-Domain", schoolData.subdomain)}
        {renderField("SMS Credits", schoolData.credit ?? 0)}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-500 mb-1">
            Terms & Conditions Agreement
          </label>
          <div
            className={`p-3 border border-gray-200 rounded-lg bg-gray-50 font-medium flex items-center gap-2 ${schoolData.isAgreeTC ? "text-green-600" : "text-red-600"
              }`}
          >
            {schoolData.isAgreeTC ? "✔ Agreed" : "✘ Not Agreed"}
          </div>
        </div>
      </div>
      <div className="flex justify-end mt-6">
        <button
          onClick={() => window.history.back()}
          className="px-6 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold transition-colors"
        >
          Back
        </button>
      </div>
    </div>
  );

  // Get the active component to render
  const activeComponent = navigationItems.find(
    (item) => item.name === activeTab
  )?.component;

  return (
    <div className="w-full h-full overflow-hidden flex flex-col ml-1">
      {/* Tab Navigation - Fixed height */}
      {
        navigationItems && navigationItems.length > 0 && (
          <div ref={tabContainerRef} className="bg-primary_main rounded-xl flex-shrink-0 mt-4">
            {console.log("visibleTabs:-", visibleTabs)}
            <div className="flex gap-2 p-2 items-center">
              {/* Visible Tabs */}
              {visibleTabs.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleTabClick(item.name)}
                  className={`flex items-center px-2 py-2 rounded-md transition-all whitespace-nowrap ${activeTab === item.name
                    ? "bg-white text-primary_main font-semibold"
                    : "text-white hover:text-primary_main hover:bg-white/10"
                    }`}
                >
                  {item.name}
                </button>
              ))}

              {/* More Button with Dropdown */}
              {hiddenTabs.length > 0 && (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`flex items-center px-3 py-2 rounded-md transition-all ${hiddenTabs.some(tab => tab.name === activeTab)
                      ? "bg-white text-primary_main font-semibold"
                      : "text-white hover:text-primary_main hover:bg-white/10"
                      }`}
                  >
                    <FiMoreHorizontal size={20} />
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 min-w-[200px] z-50">
                      {hiddenTabs.map((item) => (
                        <button
                          key={item.name}
                          onClick={() => handleTabClick(item.name)}
                          className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors first:rounded-t-lg last:rounded-b-lg ${activeTab === item.name
                            ? "bg-primary_main text-white font-semibold hover:bg-primary_main"
                            : "text-gray-700"
                            }`}
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>)}

      {/* Tab Content - Scrollable */}
      <div className="flex-1 overflow-y-auto mt-2">
        <div className="w-full h-full">
          {activeTab === "School Details" ? renderSchoolDetails() : activeComponent}
        </div>
      </div>
    </div>
  );
};

export default ViewSchool;